/**
 * AdminDashboard Component
 * 
 * Main admin interface with sidebar navigation, profile management, and content sections.
 * Features:
 * - Responsive sidebar with collapsible menus
 * - Multi-section content management
 * - User profile display
 * - Message system integration
 * - Student registration workflow
 * 
 * @component
 * @returns {React.ReactElement} Complete admin dashboard layout
 */
import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  Mail,
  MailPlus,
  MessageSquare,
  ArrowLeft,
  Users,
  Home,
  University,
  FileText,
  BookOpen,
  UserPlus,
  User as UserIcon,
  ChevronDown,
  FileUp,
  Menu,
  X,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

// Unified Theme
import { UNIFIED_THEME } from "../styles/unifiedTheme";

// Assets
import Profile from "../assets/profile";
import ChangeP from "../assets/changepassword";
import LogoutSection from "../assets/logout";
import Tree from "../assets/TreeRelation";
import logo from "../assets/logo.png";
import Footer from "../assets/Footer";

// Admin Forms & Components
import AddUserForm from "../pages/Admin/adduser";
import AllUserForm from "../pages/Admin/alluser";
import AddResidencyForm from "../pages/Admin/AddResidency";
import AllResidencyForm from "../pages/Admin/AllResidency";
import AddUniversityForm from "../pages/Admin/AddUniversity";
import AllUniversityForm from "../pages/Admin/AllUniversities";
import AddProgrammeGroupForm from "../pages/Admin/AddProgrammeGroup";
import AllProgrammeGroupForm from "../pages/Admin/AllProgrammeGroups";
import AddProgrammeForm from "../pages/Admin/AddNewProgram";
import AllProgrammeForm from "../pages/Admin/AllProgrammes";
import AllStudentForm from "../pages/Admin/All-Stnts";
import AddGroup from "../pages/Admin/AddGroup";
import AllGroup from "../pages/Admin/Allgroups";
import Addcities from "../pages/Admin/addcities";
import Allcities from "../pages/Admin/Allcities";

// Registration Steps
import Step1 from "../pages/Registration/RegisterForm";
import Step2 from "../pages/Registration/RegisterPage1";
import Step3 from "../pages/Registration/RegisterPage2";
import Step4 from "../pages/Registration/RegisterPage3";
import Step5 from "../pages/Registration/RegisterPage4";

// Message Components
import MessagesInbox from "../pages/Admin/MessagesInbox";
import MessageThread from "../pages/Admin/MessageThread";
import NewMessage from "../pages/Admin/NewMessage";

// Dashboard
import AdminSummaryDashboard from "../pages/Admin/Dashboard";

/**
 * Theme configuration object for consistent styling across dashboard
 * Now uses UNIFIED_THEME from unifiedTheme.js for consistency
 */
// UNIFIED_THEME is already imported above

export default function AdminDashboard() {
  // ==================== STATE ====================
  // Sidebar & Navigation
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState(null); // Single string for currently expanded menu
  const [activeSection, setActiveSection] = useState(() => {
    return localStorage.getItem("adminActiveSection") || "dashboard";
  });
  const [navigationParams, setNavigationParams] = useState(null);

  // UI State
  const [isDesktop, setIsDesktop] = useState(false);
  const [changeStatus, setChangeStatus] = useState(null);

  // Registration & Messaging
  const [regStep, setRegStep] = useState(1);
  const [inboxRefreshTrigger, setInboxRefreshTrigger] = useState(0);

  // ==================== REFS ====================
  const sidebarRef = useRef(null);
  const hamburgerRef = useRef(null);

  // ==================== NAVIGATION ====================
  const navigate = useNavigate();

  // ==================== ANIMATION CONFIG ====================
  const spring = { type: "spring", stiffness: 200, damping: 24 };

  // ==================== CALLBACKS ====================
  // NOTE: Callbacks must be defined BEFORE Effects that use them

  /**
   * Toggle sidebar open/closed state
   */
  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  /**
   * Close sidebar
   */
  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  /**
   * Set active section with optional navigation parameters
   * @param {string} section - Section key to activate
   * @param {Object} params - Optional parameters for navigation
   */
  const handleSetActiveSection = useCallback((section, params = null) => {
    setActiveSection(section);
    setNavigationParams(params || null);
  }, []);

  /**
   * Trigger inbox refresh by incrementing refresh counter
   */
  const triggerInboxRefresh = useCallback(() => {
    setInboxRefreshTrigger((prev) => prev + 1);
  }, []);

  /**
   * Toggle menu expansion state
   * @param {string} key - Menu key to toggle
   */
  const toggleMenu = useCallback((key) => {
    setExpandedMenu((prev) => (prev === key ? null : key));
  }, []);

  // ==================== EFFECTS ====================

  /**
   * Save activeSection to localStorage when it changes
   */
  useEffect(() => {
    localStorage.setItem("adminActiveSection", activeSection);
  }, [activeSection]);

  /**
   * Clear registration data when leaving add-student section
   */
  useEffect(() => {
    if (activeSection !== "add-student") {
      localStorage.removeItem("register_step1");
      localStorage.removeItem("register_step2");
      localStorage.removeItem("register_step3");
      localStorage.removeItem("register_step4");
    }
  }, [activeSection]);

  /**
   * Clear student list data when leaving student-list section
   */
  useEffect(() => {
    if (activeSection !== "student-list") {
      localStorage.removeItem("register_1");
      localStorage.removeItem("student_id");
    }
  }, [activeSection]);

  /**
   * Handle window resize for responsive design
   */
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /**
   * Handle escape key to close sidebar
   */
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeSidebar();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [closeSidebar]);

  // ==================== MEMOIZED DATA ====================

  /**
   * User profile data from localStorage
   * Memoized to prevent unnecessary re-renders
   */
  const user = useMemo(() => {
    try {
      const userData = JSON.parse(localStorage.getItem("user")) || {};
      const fullName = userData.name || "";
      const [firstName = "", lastName = ""] = fullName.split(" ", 2);
      return {
        id: userData.id || "N/A",
        firstName,
        lastName,
        university: userData.university || "N/A",
        course: userData.course || "N/A",
        intake: userData.intake || "N/A",
        status: userData.status || "N/A",
        role: userData.role || "N/A",
        email: userData.email || "N/A",
        mobile: userData.mobile || "N/A",
      };
    } catch (error) {
      console.error("Error parsing user data:", error);
      return {
        id: "N/A",
        firstName: "Guest",
        lastName: "User",
        university: "N/A",
        course: "N/A",
        intake: "N/A",
        status: "N/A",
        role: "N/A",
        email: "N/A",
        mobile: "N/A",
      };
    }
  }, []);

  // ==================== COMPONENTS ====================

  /**
   * SidebarLink - Individual sidebar navigation link
   * @component
   * @param {Object} props
   * @param {React.ElementType} props.icon - Icon component
   * @param {string} props.label - Link label
   * @param {string} props.sectionKey - Section identifier
   */
  const SidebarLink = useCallback(
    ({ icon: Icon, label, sectionKey }) => (
      <motion.button
        onClick={() => {
          handleSetActiveSection(sectionKey);
          if (!isDesktop) {
            closeSidebar();
          }
        }}
        className={`w-full text-left flex items-center gap-2 text-sm py-2 px-3 rounded-lg transition-colors duration-300 ${
          activeSection === sectionKey
            ? `bg-blue-600 text-white shadow-md`
            : `hover:bg-gray-700 text-gray-100`
        }`}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        transition={spring}
        aria-current={activeSection === sectionKey ? "page" : undefined}
      >
        <Icon size={14} aria-hidden="true" />
        <span className="truncate">{label}</span>
      </motion.button>
    ),
    [activeSection, handleSetActiveSection, isDesktop, closeSidebar]
  );

  /**
   * CollapsibleMenu - Expandable menu section with sub-items
   * @component
   * @param {Object} props
   * @param {string} props.title - Menu title
   * @param {React.ElementType} props.icon - Icon component
   * @param {string} props.keyName - Unique menu identifier
   * @param {Array} props.items - Menu items array with icon, label, and key
   */
  const CollapsibleMenu = useCallback(
    ({ title, icon: Icon, keyName, items }) => {
      const isAnyChildActive = items.some((item) => item.key === activeSection);
      const isExpanded = expandedMenu === keyName;

      return (
        <div className="w-full">
          {/* Button */}
          <motion.button
            className={`flex items-center justify-between w-full px-3 py-2 gap-2 rounded-lg transition-all duration-300 ${
              isAnyChildActive
                ? "bg-blue-600 text-white"
                : "text-gray-100 hover:bg-gray-700 hover:text-white"
            } text-sm focus:outline-none focus:ring-2 focus:ring-blue-400`}
            onClick={() => toggleMenu(keyName)}
            aria-expanded={isExpanded}
            aria-controls={`${keyName}-menu`}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.97 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <div className="flex items-center gap-2 overflow-hidden">
              <Icon size={14} aria-hidden="true" className="shrink-0" />
              <span className="truncate">{title}</span>
            </div>

            {/* Arrow animation */}
            <motion.div
              className="ml-auto"
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ type: "tween", ease: "easeInOut", duration: 0.3 }}
              aria-hidden="true"
            >
              <ChevronDown size={14} />
            </motion.div>
          </motion.button>

          {/* Animated dropdown */}
          <AnimatePresence initial={false} mode="wait">
            {isExpanded && (
              <motion.div
                key={`${keyName}-content`}
                id={`${keyName}-menu`}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="ml-4 mt-1 overflow-hidden space-y-1"
                transition={{ duration: 0.4, ease: "easeInOut" }}
              >
                {items.map(({ icon, label, key }, idx) => (
                  <motion.div
                    key={key}
                    initial={{ x: -10, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: -10, opacity: 0 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <SidebarLink
                      icon={icon}
                      label={label}
                      sectionKey={key}
                      isActive={key === activeSection}
                    />
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    },
    [expandedMenu, toggleMenu, SidebarLink, activeSection]
  );

  /**
   * Render main content based on active section
   * Routes to appropriate component for each dashboard section
   * @returns {React.ReactElement} Content component for current section
   */
  const renderContent = useCallback(() => {
    if (activeSection === "admin-status") {
      return <AdminSummaryDashboard />;
    }

    // Message-related sections
    if (activeSection === "messages-inbox") {
      return (
        <div className="space-y-4">
          <MessagesInbox
            setActiveSection={handleSetActiveSection}
            refreshTrigger={inboxRefreshTrigger}
          />
        </div>
      );
    }

    if (activeSection === "new-message") {
      return (
        <div className="space-y-4">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex items-center justify-between"
          >
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <MailPlus size={20} className="text-blue-600" />
              New Message
            </h2>
          </motion.div>
          <NewMessage setActiveSection={handleSetActiveSection} />
        </div>
      );
    }

    // Handle message thread with navigation parameters
    if (activeSection === "message-thread") {
      if (!navigationParams) {
        console.error("No navigation parameters for message thread");
        return (
          <div className="space-y-4">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              Error: Missing thread information. Please go back to inbox and try
              again.
            </div>
            <button
              onClick={() => handleSetActiveSection("messages-inbox")}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Inbox
            </button>
          </div>
        );
      }

      return (
        <div className="space-y-4">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex items-center justify-between flex-wrap gap-2"
          >
            <button
              onClick={() => handleSetActiveSection("messages-inbox")}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors text-sm"
            >
              <ArrowLeft size={16} /> Back to Inbox
            </button>
            <h2 className="text-lg font-bold text-gray-800">
              Conversation with {navigationParams.recipientName || "User"}
            </h2>
            <div></div> {/* Empty div for balance */}
          </motion.div>
          <MessageThread
            threadId={navigationParams.threadId}
            recipientId={navigationParams.recipientId}
            currentUserId={navigationParams.currentUserId}
            recipientName={navigationParams.recipientName}
            setActiveSection={handleSetActiveSection}
            triggerInboxRefresh={triggerInboxRefresh}
          />
        </div>
      );
    }

    // Handle old URL-style message thread (for backward compatibility)
    if (activeSection.startsWith("message-thread/")) {
      const threadId = activeSection.split("/")[1];
      return (
        <div className="space-y-4">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex items-center justify-between flex-wrap gap-2"
          >
            <button
              onClick={() => handleSetActiveSection("messages-inbox")}
              className="flex items-center gap-2 text-blue-600 text-sm"
            >
              <ArrowLeft size={16} /> Back to Inbox
            </button>
            <h2 className="text-lg font-bold text-gray-800">Conversation</h2>
            <div></div> {/* Empty div for balance */}
          </motion.div>
          <MessageThread
            threadId={threadId}
            setActiveSection={handleSetActiveSection}
            triggerInboxRefresh={triggerInboxRefresh}
          />
        </div>
      );
    }

    // Student registration steps
    if (activeSection === "add-student") {
      const steps = [Step1, Step2, Step3, Step4, Step5];
      const CurrentStep = steps[regStep - 1];

      return (
        <motion.div
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 max-w-6xl mx-auto"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
            <UserPlus size={20} className="text-blue-600" />
            Student Registration
          </h2>

          <CurrentStep
            onNext={() => setRegStep((s) => Math.min(s + 1, steps.length))}
            onBack={() => setRegStep((s) => Math.max(s - 1, 1))}
          />
        </motion.div>
      );
    }

    const components = {
      profile: (
        <div className="bg-white shadow-sm rounded-lg p-4 max-w-4xl mx-auto">
          <h2 className="text-lg font-bold mb-4 text-gray-800">
            Admin Profile
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-semibold text-gray-500 mb-1">
                  First Name
                </label>
                <div className="text-sm text-gray-800 border border-gray-200 rounded px-3 py-2 bg-gray-50">
                  {user.firstName}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-500 mb-1">
                  Email
                </label>
                <div className="text-sm text-gray-800 border border-gray-200 rounded px-3 py-2 bg-gray-50">
                  {user.email}
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-semibold text-gray-500 mb-1">
                  Last Name
                </label>
                <div className="text-sm text-gray-800 border border-gray-200 rounded px-3 py-2 bg-gray-50">
                  {user.lastName}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-500 mb-1">
                  Role
                </label>
                <div className="text-sm text-gray-800 border border-gray-200 rounded px-3 py-2 bg-gray-50">
                  {user.role}
                </div>
              </div>
            </div>
          </div>
          <hr className="my-6" />
          <section className="bg-gray-50 rounded-lg p-4 shadow-inner">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Change Password
            </h2>

            {changeStatus && (
              <p
                className={`mb-3 font-medium ${
                  changeStatus.type === "success"
                    ? "text-green-600"
                    : "text-red-500"
                }`}
              >
                {changeStatus.message}
              </p>
            )}

            <ChangeP email={user?.email} />
          </section>
        </div>
      ),
      "add-user": <AddUserForm theme={UNIFIED_THEME} />,
      "all-users": <AllUserForm theme={UNIFIED_THEME} />,
      tree: <Tree />,
      "add-residency": <AddResidencyForm theme={UNIFIED_THEME} />,
      "all-residencies": <AllResidencyForm theme={UNIFIED_THEME} />,
      "add-university": <AddUniversityForm theme={UNIFIED_THEME} />,
      "all-universities": <AllUniversityForm theme={UNIFIED_THEME} />,
      "add-programme-group": <AddProgrammeGroupForm theme={UNIFIED_THEME} />,
      "all-programme-groups": <AllProgrammeGroupForm theme={UNIFIED_THEME} />,
      "add-city": <Addcities theme={UNIFIED_THEME} />,
      "all-cities": <Allcities theme={UNIFIED_THEME} />,
      "add-groups": <AddGroup theme={UNIFIED_THEME} />,
      "all-groups": <AllGroup theme={UNIFIED_THEME} />,
      "add-programme": <AddProgrammeForm theme={UNIFIED_THEME} />,
      "all-programmes": <AllProgrammeForm theme={UNIFIED_THEME} />,
      "student-list": <AllStudentForm theme={UNIFIED_THEME} />,
      dashboard: <AdminSummaryDashboard />,
    };

    return components[activeSection] || components.dashboard;
  }, [
    activeSection,
    regStep,
    user,
    navigationParams,
    inboxRefreshTrigger,
    handleSetActiveSection,
    triggerInboxRefresh,
    changeStatus,
  ]);

  /**
   * HamburgerMenu - Toggle button for mobile sidebar
   * Animated button that switches between menu and close icons
   */
  const HamburgerMenu = useMemo(
    () => (
      <motion.button
        ref={hamburgerRef}
        onClick={toggleSidebar}
        className="
  p-2 rounded-xl bg-blue-600/20 
  text-blue-300 
  backdrop-blur-md 
  border border-blue-500/20
  hover:bg-blue-600/30 
  hover:text-white
  transition-all duration-300
  focus:outline-none 
  focus:ring-2 focus:ring-blue-400/50
  active:scale-95
"
        aria-label={sidebarOpen ? "Close menu" : "Open menu"}
        whileTap={{ scale: 0.95 }}
      >
        <AnimatePresence mode="wait">
          {sidebarOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <X size={18} />
            </motion.div>
          ) : (
            <motion.div
              key="menu"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Menu size={18} />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
    ),
    [sidebarOpen, toggleSidebar]
  );

  /**
   * TopBar - Mobile header showing user status and greeting
   * Displays only on mobile devices (hidden on md and up)
   */
  const TopBar = useMemo(() => {
    // Get user data from localStorage
    const getUserData = () => {
      try {
        const userJson = localStorage.getItem("user");
        if (userJson) {
          const user = JSON.parse(userJson);
          return {
            name: user.name || "Guest User",
            firstName: user.name?.split(" ")[0] || "User",
            profilePhoto: user.profilePhoto || null,
          };
        }
      } catch (error) {
        console.error("Error parsing user:", error);
      }
      return { name: "Guest User", firstName: "User", profilePhoto: null };
    };

    const userData = getUserData();

    return (
      <div className="fixed top-0 left-0 right-0 z-40 p-3 flex items-center justify-between md:hidden bg-white shadow-sm border-b border-gray-200">
        {HamburgerMenu}
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-green-500"></span>
          <span className="text-sm font-medium text-gray-700">
            Hello, {userData.firstName}
          </span>
        </div>
      </div>
    );
  }, [HamburgerMenu]);

  /**
   * UserProfile - Admin user profile card with name, role, and avatar
   * Displays in sidebar with animated entrance
   */
  const UserProfile = useMemo(
    () => (
      <motion.div
        className="relative flex flex-col items-center bg-gradient-to-br from-gray-800 via-gray-900 to-black p-3 rounded-lg border border-gray-700 min-h-[140px] w-full"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        {/* Profile Image */}
        <div className="relative">
          <Profile userId={user.id} />
          {/* Static Status Dot */}
          <span className="absolute bottom-1 right-1 w-2 h-2 bg-green-500 border border-gray-900 rounded-full"></span>
        </div>

        {/* User Name */}
        <h2 className="mt-2 text-white text-sm font-semibold text-center px-2 line-clamp-2">
          {user.firstName} {user.lastName}
        </h2>

        {/* Role */}
        <p className="mt-1 text-xs text-blue-300 bg-blue-900/30 px-2 py-1 rounded-full">
          {user.role}
        </p>
      </motion.div>
    ),
    [user]
  );

  /**
   * SidebarContent - Main sidebar navigation structure
   * Includes user profile card, navigation links, and collapsible menus
   */
  const SidebarContent = useMemo(
    () => (
      <div className="overflow-y-auto flex flex-col h-full">
        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between p-3 border-b border-gray-700">
          <span className="text-white font-semibold text-sm">Menu</span>
          {HamburgerMenu}
        </div>

        {/* User Profile Section */}
        <div className="px-2 py-3">{UserProfile}</div>

        {/* Navigation Menu */}
        <nav className="px-1 py-1 flex-grow space-y-1">
          <SidebarLink icon={Home} label="Dashboard" sectionKey="dashboard" />
          <SidebarLink icon={UserIcon} label="Profile" sectionKey="profile" />

          {/* Collapsible Menus */}
          <CollapsibleMenu
            title="Users"
            icon={Users}
            keyName="users"
            items={[
              { icon: UserPlus, label: "Add User", key: "add-user" },
              { icon: UserIcon, label: "All Users", key: "all-users" },
              { icon: UserIcon, label: "Relationships", key: "tree" },
            ]}
          />

          <CollapsibleMenu
            title="Residency"
            icon={FileUp}
            keyName="residency"
            items={[
              { icon: FileText, label: "Add Residency", key: "add-residency" },
              {
                icon: FileText,
                label: "All Residencies",
                key: "all-residencies",
              },
              {
                icon: BookOpen,
                label: "Add City",
                key: "add-city",
              },
              {
                icon: BookOpen,
                label: "All Cities",
                key: "all-cities",
              },
            ]}
          />

          <CollapsibleMenu
            title="Universities"
            icon={University}
            keyName="universities"
            items={[
              {
                icon: University,
                label: "Add University",
                key: "add-university",
              },
              {
                icon: University,
                label: "All Universities",
                key: "all-universities",
              },
            ]}
          />

          <CollapsibleMenu
            title="Programme Groups"
            icon={BookOpen}
            keyName="programme-group"
            items={[
              {
                icon: BookOpen,
                label: "Add Programme Group",
                key: "add-programme-group",
              },
              {
                icon: BookOpen,
                label: "All Programme Groups",
                key: "all-programme-groups",
              },
              {
                icon: BookOpen,
                label: "Add Groups",
                key: "add-groups",
              },
              {
                icon: BookOpen,
                label: "All Groups",
                key: "all-groups",
              },
            ]}
          />

          <CollapsibleMenu
            title="Programmes"
            icon={BookOpen}
            keyName="programmes"
            items={[
              { icon: BookOpen, label: "Add Programme", key: "add-programme" },
              {
                icon: BookOpen,
                label: "All Programmes",
                key: "all-programmes",
              },
            ]}
          />

          <CollapsibleMenu
            title="Students"
            icon={Users}
            keyName="students"
            items={[
              { icon: UserPlus, label: "Add Student", key: "add-student" },
              { icon: UserIcon, label: "All Students", key: "student-list" },
            ]}
          />

          <CollapsibleMenu
            title="Messages"
            icon={MessageSquare}
            keyName="messages"
            items={[
              { icon: Mail, label: "Inbox", key: "messages-inbox" },
              { icon: MailPlus, label: "New Message", key: "new-message" },
            ]}
          />
        </nav>

        {/* Logout Section */}
        <div className="mt-auto p-2">
          <LogoutSection navigate={navigate} />
        </div>
      </div>
    ),
    [SidebarLink, CollapsibleMenu, UserProfile, navigate, HamburgerMenu]
  );

  // ==================== RENDER ====================
  /**
   * Layout Structure:
   * 1. TopBar (Mobile-only) - Shows user greeting and hamburger
   * 2. Mobile Overlay - Semi-transparent overlay when sidebar is open
   * 3. Sidebar - Collapsible navigation with menus
   * 4. Main Content Area - Header, content, and footer
   * 
   * Responsive Behavior:
   * - Mobile (< 768px): Sidebar slides in from left, overlay covers main content
   * - Desktop (>= 768px): Sidebar always visible, no overlay
   */
  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
      {TopBar}

      <AnimatePresence>
        {sidebarOpen && !isDesktop && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeSidebar}
          />
        )}
      </AnimatePresence>

      {/* Sidebar - Fully closes on desktop, slides on mobile */}
      <motion.aside
        ref={sidebarRef}
        className={`fixed inset-y-0 left-0 z-40 bg-gray-900 shadow-xl flex flex-col w-64 md:relative md:flex-shrink-0 overflow-hidden md:overflow-visible ${
          sidebarOpen ? "md:w-64" : "md:w-0"
        }`}
        initial={false}
        animate={{
          x: sidebarOpen ? 0 : "-100%",
          width: sidebarOpen ? 256 : 0,
        }}
        transition={{
          type: "tween",
          ease: "easeInOut",
          duration: 0.4,
        }}
      >
        {SidebarContent}
      </motion.aside>

      {/* Main Content */}
      <div
        className={`flex flex-col flex-1 overflow-hidden transition-all duration-300 pt-12 md:pt-0 min-h-screen`}
      >
        {/* Desktop Header with Hamburger */}
        <div className="hidden md:flex relative items-center bg-gray-900 shadow-lg border-b border-gray-800/40 py-3 px-6">
          <div className="flex items-center gap-4">
            {HamburgerMenu}

            <div className="flex items-center gap-3">
              {/* Logo */}
              <img
                src={`${logo}`}
                alt="GCRD Logo"
                className="h-7 w-auto transition-all duration-300 hover:scale-110 drop-shadow-lg filter"
              />
            </div>
          </div>

          {/* This pushes user info to far right */}
          <div className="flex-1"></div>

          {/* User Info */}
          <div className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
            </span>
            <span className="text-sm font-medium text-gray-200">
              Hello, {user.firstName}
            </span>
          </div>
        </div>

        <motion.header
          className="p-3 md:p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.6 } }}
        >
          <h1 className="text-base md:text-lg font-bold text-gray-800 flex flex-wrap items-center gap-1">
            <span>Admin Dashboard</span>
            <span className="text-gray-600 font-normal text-sm">
              -{" "}
              {activeSection === "add-student"
                ? "Add Student"
                : activeSection === "profile"
                ? "Profile"
                : activeSection.startsWith("messages")
                ? "Messages"
                : "Overview"}
            </span>
          </h1>
        </motion.header>

        <motion.main
          className="flex-1 p-3 md:p-4 overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 min-h-[calc(100vh-8rem)]">
            {renderContent()}
          </div>
        </motion.main>

        <Footer />
      </div>
    </div>
  );
}
